import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TreeViewItemId } from '@mui/x-tree-view';

import { IIngestion, IIngestionDataItem, ILogDetails } from './ingestion.interface';
import { initialIngestionState } from './ingestion.constants';
import { setTreeIds } from './helpers';

export const ingestionLogDetailsReducer = {
  resetDetails: (state: IIngestion) => {
    state.ingestionLogDetails.details = initialIngestionState.ingestionLogDetails.details;
    state.ingestionLogDetails.stage = initialIngestionState.ingestionLogDetails.stage;
    state.ingestionLogDetails.dsName = initialIngestionState.ingestionLogDetails.dsName;
    state.ingestionLogDetails.tableName = initialIngestionState.ingestionLogDetails.tableName;
    state.ingestionLogDetails.fileName = initialIngestionState.ingestionLogDetails.fileName;
    state.ingestionLogDetails.level = initialIngestionState.ingestionLogDetails.level;
    state.ingestionLogDetails.isLoading = initialIngestionState.ingestionLogDetails.isLoading;
    state.ingestionLogDetails.hasError = initialIngestionState.ingestionLogDetails.hasError;
    state.ingestionLogDetails.isMessageExpanded =
      initialIngestionState.ingestionLogDetails.isMessageExpanded;
  },

  initFetchLogDetails: (state: IIngestion) => {
    state.ingestionLogDetails.isLoading = true;
    state.ingestionLogDetails.hasError = false;
  },

  successFetchLogDetails: (state: IIngestion, action: PayloadAction<ILogDetails>) => {
    const detailsWithTreeId: ILogDetails = setTreeIds(action.payload);

    state.ingestionLogDetails.isLoading = false;
    state.ingestionLogDetails.details = detailsWithTreeId;
  },

  errorFetchLogDetails: (state: IIngestion) => {
    state.ingestionLogDetails.isLoading = false;
    state.ingestionLogDetails.hasError = true;
  },

  setMessageIsExpanded: (state: IIngestion, action: PayloadAction<boolean>) => {
    state.ingestionLogDetails.isMessageExpanded = action.payload;
  },

  setStage: (state: IIngestion, action: PayloadAction<string>) => {
    state.ingestionLogDetails.stage = action.payload;
  },
  setDSName: (state: IIngestion, action: PayloadAction<string>) => {
    state.ingestionLogDetails.dsName = action.payload;
  },
  setTableName: (state: IIngestion, action: PayloadAction<string>) => {
    state.ingestionLogDetails.tableName = action.payload;
  },
  setFileName: (state: IIngestion, action: PayloadAction<string>) => {
    state.ingestionLogDetails.fileName = action.payload;
  },
  setLevel: (state: IIngestion, action: PayloadAction<string>) => {
    state.ingestionLogDetails.level = action.payload;
  },

  setTriplesLevel: (state: IIngestion) => {
    state.ingestionLogDetails.stage = 'Triples';
    state.ingestionLogDetails.level = 'Triples';

    // clear all others
    state.ingestionLogDetails.dsName = null;
    state.ingestionLogDetails.tableName = null;
    state.ingestionLogDetails.fileName = null;
  },

  setTimeseriesLevel: (state: IIngestion) => {
    state.ingestionLogDetails.stage = 'Timeseries';
    state.ingestionLogDetails.level = 'Timeseries';

    // clear all others
    state.ingestionLogDetails.dsName = null;
    state.ingestionLogDetails.tableName = null;
    state.ingestionLogDetails.fileName = null;
  },

  setDataSourceLevel: (state: IIngestion, action: PayloadAction<IIngestionDataItem['name']>) => {
    state.ingestionLogDetails.level = action.payload;
    state.ingestionLogDetails.dsName = action.payload;

    // clear all others
    state.ingestionLogDetails.tableName = null;
    state.ingestionLogDetails.fileName = null;
  },

  setTableFileLevel: (state: IIngestion, action: PayloadAction<IIngestionDataItem['name']>) => {
    state.ingestionLogDetails.level = action.payload;
  },

  setFileLevel: (state: IIngestion, action: PayloadAction<IIngestionDataItem['name']>) => {
    state.ingestionLogDetails.level = action.payload;
    state.ingestionLogDetails.fileName = action.payload;

    // clear tableName
    state.ingestionLogDetails.tableName = null;
  },

  setTableLevel: (state: IIngestion, action: PayloadAction<IIngestionDataItem['name']>) => {
    state.ingestionLogDetails.level = action.payload;
    state.ingestionLogDetails.tableName = action.payload;

    // clear fileName
    state.ingestionLogDetails.fileName = null;
  },

  // Ingestion log filtering
  resetLogFiltering: (state: IIngestion) => {
    state.logFiltering.searchTerm = initialIngestionState.logFiltering.searchTerm;
    state.logFiltering.statusId = initialIngestionState.logFiltering.statusId;
  },

  setLogFilteringSearchTerm: (state: IIngestion, action: PayloadAction<string>) => {
    state.logFiltering.searchTerm = action.payload;
  },

  setLogFilteringStatusId: (state: IIngestion, action: PayloadAction<number | null>) => {
    state.logFiltering.statusId = action.payload;
  },

  // selected log id
  setSelectedLogId: (state: IIngestion, action: PayloadAction<string | null>) => {
    state.selectedLogId = action.payload;
  },

  resetSelectedLogId: (state: IIngestion) => {
    state.selectedLogId = initialIngestionState.selectedLogId;
  },

  // tree view - expanded leafs and selected item
  resetSelectedTreeItems: (state: IIngestion) => {
    state.selectedTreeItem = initialIngestionState.selectedTreeItem;
    state.expandedTreeItems = initialIngestionState.expandedTreeItems;
    state.selectedLogId = initialIngestionState.selectedLogId;

    // also reset ingestionLogDetails since we are resetting the selected log
    state.ingestionLogDetails = { ...initialIngestionState.ingestionLogDetails };
  },

  setSelectedTreeItem: (state: IIngestion, action: PayloadAction<TreeViewItemId>) => {
    state.selectedTreeItem = action.payload;
  },

  initExpandedTreeItem: (state: IIngestion, action: PayloadAction<TreeViewItemId>) => {
    state.selectedTreeItem = action.payload;
    state.expandedTreeItems = [action.payload];
  },

  addExpandedTreeItem: (state: IIngestion, action: PayloadAction<TreeViewItemId>) => {
    if (!state.expandedTreeItems.includes(action.payload)) {
      state.expandedTreeItems.push(action.payload);
    }
  },

  removeExpandedTreeItem: (state: IIngestion, action: PayloadAction<TreeViewItemId>) => {
    state.expandedTreeItems = state.expandedTreeItems.filter((i) => i !== action.payload);
  },

  resetTreeAndSelectedLog: (state: IIngestion) => {
    state.selectedLogId = initialIngestionState.selectedLogId;
    state.selectedTreeItem = initialIngestionState.selectedTreeItem;
    state.expandedTreeItems = initialIngestionState.expandedTreeItems;
    state.logFiltering.searchTerm = initialIngestionState.logFiltering.searchTerm;
    state.logFiltering.statusId = initialIngestionState.logFiltering.statusId;
  },
};

export const IngestionSlice = createSlice({
  name: 'ingestion',
  initialState: initialIngestionState,
  reducers: {
    ...ingestionLogDetailsReducer,
  },
});

export const {
  setMessageIsExpanded,
  setStage,
  setDSName,
  setTableName,
  setFileName,
  setLevel,
  setTriplesLevel,
  setTimeseriesLevel,
  setDataSourceLevel,
  setTableFileLevel,
  setFileLevel,
  setTableLevel,
  resetDetails,
  initFetchLogDetails,
  successFetchLogDetails,
  errorFetchLogDetails,

  resetLogFiltering,
  setLogFilteringSearchTerm,
  setLogFilteringStatusId,
  setSelectedLogId,
  resetSelectedLogId,
  resetSelectedTreeItems,
  setSelectedTreeItem,
  initExpandedTreeItem,
  addExpandedTreeItem,
  removeExpandedTreeItem,
  resetTreeAndSelectedLog,
} = IngestionSlice.actions;

export default IngestionSlice.reducer;
